import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Globe2,
  HeartHandshake,
  Mail,
  Quote,
  Tag,
  User,
} from "lucide-react";
import { fallbackBlogs, getFallbackBlog } from "../data/fallbackBlogs";
import { localizedUrl } from "../lib/fetchLocalized";

const fallbackImage = "/assets/agm/swamiji-vedant.jpg";

const readingPath = [
  {
    label: "Vedant lens",
    text: "Start with the source idea: dignity, equal vision, skill in action or lokasangraha.",
  },
  {
    label: "NGO translation",
    text: "Ask how that idea appears in a clinic, classroom, skilling lab, wedding support or relief camp.",
  },
  {
    label: "USA action",
    text: "Convert inspiration into recurring support, professional mentorship, reporting help or volunteer work.",
  },
];

const BlogsDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      const localBlog = getFallbackBlog(id);

      try {
        setLoading(true);
        setError(null);

        const baseUrl = import.meta.env.VITE_APP_BASE_URL;
        if (!baseUrl) {
          if (localBlog) {
            setBlog(localBlog);
          } else {
            setError("Blog post not found.");
          }
          return;
        }

        const response = await axios.get(localizedUrl(`${baseUrl}/blogs/${id}`));
        if (response.data.success && response.data.data) {
          setBlog(response.data.data);
        } else if (localBlog) {
          setBlog(localBlog);
        } else {
          setError("Blog post not found.");
        }
      } catch (err) {
        console.error("Error fetching blog details:", err);
        if (localBlog) {
          setBlog(localBlog);
        } else {
          setError("Failed to fetch blog. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  const relatedBlogs = useMemo(
    () => fallbackBlogs.filter((item) => item._id !== id).slice(0, 3),
    [id]
  );

  const formatDate = (dateString) => {
    if (!dateString) return "AGM editorial";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "AGM editorial";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const imageGallery = useMemo(() => {
    const images = blog?.images?.length ? blog.images : [fallbackImage];
    return [...new Set(images.filter(Boolean))];
  }, [blog]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#fbf7f1] pt-36 text-[#241511]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.72fr]">
            <div className="h-[420px] animate-pulse rounded-lg bg-[#eadccc]" />
            <div className="h-[420px] animate-pulse rounded-lg bg-white" />
          </div>
        </div>
      </main>
    );
  }

  if (error || !blog) {
    return (
      <main className="min-h-screen bg-[#fbf7f1] pt-36 text-[#241511]">
        <div className="container mx-auto px-4 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#7f1916]">
            AGM Journal
          </p>
          <h1 className="mt-4 text-4xl font-semibold">This article is not available yet.</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-[#6f5a4f]">
            {error || "The requested article could not be found."}
          </p>
          <Link
            to="/blogs"
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#7f1916] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white"
          >
            <ArrowLeft size={17} />
            Back to blogs
          </Link>
        </div>
      </main>
    );
  }

  const mainImage = imageGallery[0] || fallbackImage;
  const hasStructuredSections = Array.isArray(blog.sections) && blog.sections.length > 0;
  const deck =
    blog.deck || "A reflection from AvdheshanandG Mission on Vedant, seva and social impact.";
  const authorName = blog.author?.username || "AGM Editorial";

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#fbf7f1] text-[#241511]">
      <style>{`
        @keyframes agmFloat {
          0%, 100% { transform: translate3d(0, 0, 0) rotate(-1deg); }
          50% { transform: translate3d(0, -12px, 0) rotate(1deg); }
        }
        .agm-float-card { animation: agmFloat 8s ease-in-out infinite; }
        .agm-article-body h2 {
          color: #4b2418;
          font-size: clamp(1.6rem, 3vw, 2.35rem);
          line-height: 1.15;
          font-weight: 700;
          margin: 2.5rem 0 1rem;
        }
        .agm-article-body p {
          color: #5f4a3e;
          font-size: 1.05rem;
          line-height: 1.95;
          margin: 1rem 0;
        }
        .agm-article-body ul {
          display: grid;
          gap: 0.75rem;
          margin: 1.25rem 0;
          padding-left: 1.2rem;
          color: #5f4a3e;
        }
        .agm-article-body li { line-height: 1.8; }
        .agm-article-body figure {
          overflow: hidden;
          border-radius: 0.5rem;
          margin: 1.6rem 0;
          border: 1px solid #e4d4c5;
          background: #fff;
        }
        .agm-article-body figure img {
          width: 100%;
          min-height: 280px;
          max-height: 520px;
          object-fit: cover;
          display: block;
        }
        .agm-article-body figcaption {
          color: #7f1916;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 0.9rem 1rem;
        }
      `}</style>

      <section className="relative overflow-hidden border-b border-[#e4d4c5] bg-[#f8efe5] pt-24 lg:pt-28">
        <div className="absolute inset-0 opacity-45">
          <div className="absolute left-[-10%] top-12 h-72 w-72 rounded-full bg-[#d9b271]/35 blur-3xl" />
          <div className="absolute bottom-[-20%] right-[-8%] h-96 w-96 rounded-full bg-[#7f1916]/12 blur-3xl" />
        </div>

        <div className="container relative z-10 mx-auto grid gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[0.96fr_1.04fr] lg:px-8 lg:py-16">
          <div className="flex flex-col justify-center">
            <Link
              to="/blogs"
              className="mb-8 inline-flex w-fit items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#7f1916]"
            >
              <ArrowLeft size={17} />
              AGM Journal
            </Link>

            <div className="flex flex-wrap gap-2">
              <span className="rounded-md bg-[#7f1916] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-white">
                {blog.category || "Vedant and Seva"}
              </span>
              {blog.audience && (
                <span className="rounded-md border border-[#d3bda8] bg-white/75 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#4b2418]">
                  {blog.audience}
                </span>
              )}
              {blog.sourceLinks?.length > 0 && (
                <span className="rounded-md border border-[#d3bda8] bg-white/75 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#4b2418]">
                  {blog.sourceLinks.length} public sources
                </span>
              )}
            </div>

            <h1 className="mt-5 max-w-4xl text-3xl font-semibold leading-tight text-[#241511] md:text-6xl">
              {blog.title}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-[#6a5144]">{deck}</p>

            <div className="mt-7 flex flex-wrap gap-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#7d6a5f]">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays size={15} />
                {formatDate(blog.createdAt)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 size={15} />
                {blog.readTime || "6 min read"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <User size={15} />
                {authorName}
              </span>
            </div>

            {blog.sourceLine && (
              <div className="mt-8 rounded-lg border border-[#d7beaa] bg-white/72 p-5">
                <div className="flex items-start gap-3">
                  <Quote className="mt-1 h-5 w-5 shrink-0 text-[#7f1916]" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                      Ancient source thread
                    </p>
                    <p className="mt-2 text-sm leading-7 text-[#5f4a3e]">{blog.sourceLine}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="relative min-h-[520px]">
            <div className="absolute inset-0 rounded-lg bg-[#4b2418]" />
            <img
              src={mainImage}
              alt={blog.title}
              className="relative h-[520px] w-full rounded-lg object-cover shadow-2xl shadow-[#4b2418]/20"
            />
            <div className="agm-float-card absolute -bottom-5 left-5 max-w-[260px] rounded-lg border border-white/45 bg-white/92 p-5 shadow-xl backdrop-blur">
              <BookOpenCheck className="h-7 w-7 text-[#7f1916]" />
              <p className="mt-3 text-sm font-semibold leading-6 text-[#4b2418]">
                Vedantic reflection becomes meaningful when it returns to a person served with
                dignity.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="container mx-auto grid gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:px-8">
          <article className="min-w-0">
            {blog.keyPoints?.length > 0 && (
              <div className="rounded-lg border border-[#e1cfc0] bg-white p-6 shadow-sm md:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#7f1916] text-white">
                    <CheckCircle2 size={22} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                      What this article gives you
                    </p>
                    <h2 className="mt-1 text-2xl font-semibold text-[#241511]">
                      Readable points before the deep dive.
                    </h2>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 md:grid-cols-3">
                  {blog.keyPoints.map((point) => (
                    <div
                      key={point}
                      className="rounded-md bg-[#fbf7f1] p-4 text-sm leading-7 text-[#5f4a3e]"
                    >
                      {point}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {imageGallery.length > 1 && (
              <div className="mt-8 grid gap-4 md:grid-cols-[1.25fr_0.75fr]">
                <img
                  src={imageGallery[1] || mainImage}
                  alt={`${blog.title} field image`}
                  className="h-72 w-full rounded-lg object-cover md:h-[430px]"
                />
                <div className="grid gap-4">
                  <img
                    src={imageGallery[2] || mainImage}
                    alt={`${blog.title} supporting image`}
                    className="h-[207px] w-full rounded-lg object-cover"
                  />
                  <div className="rounded-lg bg-[#4b2418] p-6 text-white">
                    <Globe2 className="h-7 w-7 text-[#f4c164]" />
                    <p className="mt-4 text-sm font-semibold uppercase tracking-[0.16em] text-[#f4c164]">
                      USA reader note
                    </p>
                    <p className="mt-3 text-sm leading-7 text-white/78">
                      This journal is written for supporters who want spiritual depth and nonprofit
                      clarity in the same place.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-10">
              {hasStructuredSections ? (
                <div className="space-y-8">
                  {blog.sections.map((section, index) => (
                    <section
                      key={section.heading}
                      className={`grid gap-6 rounded-lg border border-[#e1cfc0] bg-white p-6 shadow-sm md:p-8 ${
                        section.image ? "lg:grid-cols-[0.78fr_1fr]" : ""
                      }`}
                    >
                      {section.image && (
                        <div className={index % 2 === 0 ? "lg:order-1" : "lg:order-2"}>
                          <img
                            src={section.image}
                            alt={section.heading}
                            className="h-72 w-full rounded-lg object-cover lg:h-full"
                          />
                        </div>
                      )}
                      <div
                        className={section.image && index % 2 === 0 ? "lg:order-2" : "lg:order-1"}
                      >
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                          Perspective {String(index + 1).padStart(2, "0")}
                        </p>
                        <h2 className="mt-3 text-3xl font-semibold leading-tight text-[#4b2418]">
                          {section.heading}
                        </h2>
                        <div className="mt-5 space-y-4">
                          {(Array.isArray(section.body) ? section.body : [section.body]).map(
                            (paragraph) => (
                              <p key={paragraph} className="text-base leading-8 text-[#5f4a3e]">
                                {paragraph}
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div
                  className="agm-article-body rounded-lg border border-[#e1cfc0] bg-white p-6 shadow-sm md:p-10"
                  dangerouslySetInnerHTML={{ __html: blog.content || "" }}
                />
              )}
            </div>

            {blog.takeaways?.length > 0 && (
              <section className="mt-8 rounded-lg bg-[#7f1916] p-6 text-white md:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#f4c164]">
                  Take-home practices
                </p>
                <h2 className="mt-3 text-3xl font-semibold">Make this reading useful today.</h2>
                <div className="mt-6 grid gap-3 md:grid-cols-3">
                  {blog.takeaways.map((item) => (
                    <div
                      key={item}
                      className="rounded-md border border-white/16 bg-white/10 p-4 text-sm leading-7 text-white/82"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {blog.tags?.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[#e1cfc0] pt-6">
                <Tag className="h-5 w-5 text-[#7f1916]" />
                {blog.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-[#f4e4d0] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#7f1916]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </article>

          <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-[#e1cfc0] bg-white p-6 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                Reader path
              </p>
              <h2 className="mt-3 text-2xl font-semibold text-[#241511]">
                Vedant finds completion in seva.
              </h2>
              <div className="mt-5 space-y-4">
                {readingPath.map((item) => (
                  <div key={item.label} className="border-l-2 border-[#7f1916] pl-4">
                    <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-[#4b2418]">
                      {item.label}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-[#6f5a4f]">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-[#241511] p-6 text-white">
              <HeartHandshake className="h-8 w-8 text-[#f4c164]" />
              <h2 className="mt-4 text-2xl font-semibold">
                Support the field work behind the words.
              </h2>
              <p className="mt-3 text-sm leading-7 text-white/72">
                Help AGM turn healthcare, education, skilling, relief and women dignity into visible
                public good across India and the global diaspora.
              </p>
              <Link
                to="/donate"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#f4c164] px-4 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#241511]"
              >
                Donate
                <ArrowRight size={16} />
              </Link>
            </div>

            {blog.sourceLinks?.length > 0 && (
              <div className="rounded-lg border border-[#e1cfc0] bg-white p-6 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">
                  Public research sources
                </p>
                <h2 className="mt-3 text-2xl font-semibold text-[#241511]">
                  Internet-backed context.
                </h2>
                <div className="mt-5 space-y-3">
                  {blog.sourceLinks.map((source) => (
                    <a
                      key={source.url}
                      href={source.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-md border border-[#e7d7c8] bg-[#fbf7f1] p-4 transition hover:border-[#7f1916] hover:bg-white"
                    >
                      <span className="flex items-start justify-between gap-3 text-sm font-semibold leading-6 text-[#4b2418]">
                        {source.label}
                        <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-[#7f1916]" />
                      </span>
                      {source.note && (
                        <span className="mt-2 block text-xs leading-5 text-[#6f5a4f]">
                          {source.note}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border border-[#e1cfc0] bg-white p-6 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7f1916]">Byline</p>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#f4e4d0] text-[#7f1916]">
                  <User size={21} />
                </span>
                <div>
                  <p className="font-semibold text-[#241511]">{authorName}</p>
                  {blog.author?.email && (
                    <a
                      href={`mailto:${blog.author.email}`}
                      className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#7f1916]"
                    >
                      <Mail size={14} />
                      {blog.author.email}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-[#e1cfc0] bg-white py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7f1916]">
                Continue reading
              </p>
              <h2 className="mt-3 text-3xl font-semibold text-[#241511]">
                More Vedant-led NGO perspectives.
              </h2>
            </div>
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#7f1916]"
            >
              View all articles
              <ArrowRight size={17} />
            </Link>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {relatedBlogs.map((item) => (
              <Link
                key={item._id}
                to={`/blogs/${item._id}`}
                className="group overflow-hidden rounded-lg border border-[#e1cfc0] bg-[#fbf7f1] transition hover:-translate-y-1 hover:shadow-xl"
              >
                <img
                  src={item.images?.[0] || fallbackImage}
                  alt={item.title}
                  className="h-48 w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="p-5">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#7f1916]">
                    {item.category}
                  </span>
                  <h3 className="mt-3 text-lg font-semibold leading-snug text-[#241511]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-[#6f5a4f]">{item.deck}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogsDetails;
